import React from 'react';
import { motion } from 'framer-motion';
import { TrendingUp, Building2, ShoppingCart, Sprout } from 'lucide-react';
const successStories = [{
  company: 'Global Technology Retailer',
  industry: 'E-Commerce',
  challenge: 'VP of eCommerce & Strategy needed to expand billion-dollar business beyond own distribution channels while protecting brand and integrity of customer experience.',
  solution: 'We developed a 3rd party marketplace strategy to solve for low margins and sales velocity of end of life product and built out business processes and technology middleware stack to minimize disruption of run rate business.',
  results: ['90% of marketplace customers net new', '150% more margin on EOL inventory', 'Over 500K units sold to date', 'Successfully integrated pilot into operations'],
  metric: '150%',
  metricLabel: 'increase in margin recovery',
  icon: ShoppingCart
}, {
  company: 'Global SaaS Company',
  industry: 'Enterprise Software',
  challenge: 'New CMO facing operational challenges and lack of clear prioritization. Needed revamped approach to business management, decision making.',
  solution: 'We re-engineered the ROB, built and managed an executive communications plan, led an organization redesign and built a common prioritization framework embedded in the strategic leadership team operating model.',
  results: ['Hit $1B+ ACV Target', 'Improved decision making clarity and velocity', 'Ended fire drill culture'],
  metric: '$1B+',
  metricLabel: 'ACV target achieved',
  icon: Building2
}, {
  company: 'High Growth SaaS B2B Startup',
  industry: 'SaaS Startup',
  challenge: 'A fast growing startup company needed to transition from founder-led sales to a repeatable, scalable process.',
  solution: 'We designed a structured account sales strategy and end to end sales motion, along with sales collateral and product and pricing strategy.',
  results: ['$5M renewal secured', 'First dedicated sales hire onboarded', 'Coached team real time to secure renewal', 'Built repeatable, sales foundation for broader team'],
  metric: '$5M',
  metricLabel: 'critical renewal closed',
  icon: Sprout
}];
export function ClientSuccessStories() {
  return <section id="results" className="relative py-32 px-6 lg:px-8 bg-gray-50 overflow-hidden" data-id="element-9">
      <div className="max-w-7xl mx-auto relative z-10" data-id="element-10">
        {/* Section header */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} className="text-center max-w-3xl mx-auto mb-20" data-id="element-11">
          <h2 className="text-4xl md:text-5xl font-bold mb-6 leading-tight" style={{
          fontFamily: 'Space Grotesk, sans-serif',
          color: 'var(--color-navy)'
        }} data-id="element-12">
            Real challenges. Real impact.
          </h2>
          <p className="text-xl leading-relaxed text-gray-600" data-id="element-13">
            See how we've helped companies turn strategic bets into scaled
            results
          </p>
        </motion.div>

        {/* Success story cards */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16" data-id="element-14">
          {successStories.map((story, index) => {
          const Icon = story.icon;
          return <motion.div key={story.company} initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6,
            delay: index * 0.15
          }} className="relative bg-white border border-gray-200 rounded-2xl p-8 hover:shadow-lg transition-all duration-300" data-id="element-15">
                {/* Metric highlight */}
                <div className="mb-8" data-id="element-16">
                  <div className="flex items-center gap-3 mb-6" data-id="element-17">
                    <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-[#0F52BA] to-[#06B6D4] flex items-center justify-center" data-id="element-18">
                      <Icon className="w-5 h-5 text-white" data-id="element-19" />
                    </div>
                    <div className="text-xs font-semibold text-[#0F52BA] uppercase tracking-wider" data-id="element-20">
                      {story.industry}
                    </div>
                  </div>
                  <div className="flex items-baseline gap-2 mb-1" data-id="element-21">
                    <span className="text-5xl font-bold" style={{
                  fontFamily: 'Space Grotesk, sans-serif',
                  color: 'var(--color-navy)'
                }} data-id="element-22">
                      {story.metric}
                    </span>
                  </div>
                  <p className="text-sm text-gray-600" data-id="element-23">{story.metricLabel}</p>
                </div>

                {/* Company */}
                <h3 className="text-xl font-bold mb-6 leading-tight" style={{
              fontFamily: 'Space Grotesk, sans-serif',
              color: 'var(--color-navy)'
            }} data-id="element-24">
                  {story.company}
                </h3>

                {/* Challenge */}
                <div className="mb-6" data-id="element-25">
                  <h4 className="text-sm font-bold mb-2 text-gray-500 uppercase tracking-wide" data-id="element-26">
                    Challenge
                  </h4>
                  <p className="text-sm leading-relaxed text-gray-700" data-id="element-27">
                    {story.challenge}
                  </p>
                </div>

                {/* Solution */}
                <div className="mb-6" data-id="element-28">
                  <h4 className="text-sm font-bold mb-2 text-gray-500 uppercase tracking-wide" data-id="element-29">
                    Approach
                  </h4>
                  <p className="text-sm leading-relaxed text-gray-700" data-id="element-30">
                    {story.solution}
                  </p>
                </div>

                {/* Key Results - compact list */}
                <div className="pt-6 border-t border-gray-200" data-id="element-31">
                  <h4 className="text-sm font-bold mb-3 text-gray-500 uppercase tracking-wide" data-id="element-32">
                    Key Results
                  </h4>
                  <ul className="space-y-2" data-id="element-33">
                    {story.results.slice(0, 3).map((result, idx) => <li key={idx} className="flex items-start gap-3 text-sm text-gray-700" data-id="element-34">
                        <span className="text-[#06B6D4] leading-relaxed" data-id="element-35">
                          •
                        </span>
                        <span className="leading-relaxed" data-id="element-36">{result}</span>
                      </li>)}
                  </ul>
                </div>
              </motion.div>;
        })}
        </div>

        {/* Bottom CTA */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6,
        delay: 0.4
      }} className="text-center" data-id="element-37">
          <p className="text-lg mb-6 leading-relaxed text-gray-600" data-id="element-38">
            Ready to write your own success story?
          </p>
          <a href="#contact" className="inline-block px-8 py-4 bg-gradient-to-r from-[#0F52BA] to-[#06B6D4] text-white text-base font-semibold rounded-full hover:shadow-xl hover:scale-105 transition-all duration-200" data-id="element-39">
            Start a Conversation
          </a>
        </motion.div>
      </div>
    </section>;
}